import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { formatKcal } from "@/lib/dietary";
import { DietaryBadges } from "@/components/DietaryBadges";

/** Stacked-bar visual showing the macro split. Each macro contributes
 * its kcal share (carbs/protein = 4 kcal/g, fat = 9 kcal/g). */
function MacroBar({ carbs, protein, fat }) {
  const c = (Number(carbs) || 0) * 4;
  const p = (Number(protein) || 0) * 4;
  const f = (Number(fat) || 0) * 9;
  const total = c + p + f;
  if (total <= 0) return null;
  const pct = (x) => `${(x / total) * 100}%`;
  return (
    <div
      data-testid="nutrition-macro-bar"
      className="mt-4 h-3 w-full overflow-hidden rounded-full bg-[#F3F0EA] flex"
      role="img"
      aria-label={`Macros: ${Math.round((c / total) * 100)}% carbs, ${Math.round((p / total) * 100)}% protein, ${Math.round((f / total) * 100)}% fat`}
    >
      <span style={{ width: pct(c), background: "#C19A6B" }} title="Carbs" />
      <span style={{ width: pct(p), background: "#5A7247" }} title="Protein" />
      <span style={{ width: pct(f), background: "#C05746" }} title="Fat" />
    </div>
  );
}

function MacroRow({ label, value, unit = "g", testId, swatch }) {
  return (
    <div
      data-testid={testId}
      className="flex items-center justify-between py-2 border-b border-[#E8E5DF] last:border-0"
    >
      <div className="flex items-center gap-2">
        {swatch && (
          <span
            className="h-2.5 w-2.5 rounded-full inline-block"
            style={{ background: swatch }}
          />
        )}
        <span className="text-sm text-[#5C625E]">{label}</span>
      </div>
      <span className="text-sm font-medium text-[#121614]">
        {value > 0 ? `${Math.round(value)} ${unit}` : "—"}
      </span>
    </div>
  );
}

export default function NutritionModal({ item, onClose }) {
  if (!item) return null;
  const hasMacros =
    (item.carbs_g || 0) > 0 || (item.protein_g || 0) > 0 || (item.fat_g || 0) > 0;
  return (
    <Dialog open={!!item} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        data-testid="nutrition-modal"
        className="max-w-md rounded-2xl p-6"
      >
        <DialogHeader>
          <DialogTitle className="font-serif-display text-2xl">
            Nutrition · {item.name}
          </DialogTitle>
          <DialogDescription className="text-xs text-[#5C625E]">
            Approximate values per serving. Allergens and macros are estimates — please
            ask staff if you have severe allergies.
          </DialogDescription>
        </DialogHeader>

        <div className="mt-3">
          <DietaryBadges tags={item.tags} testIdPrefix="nutrition-diet" />
        </div>

        <MacroBar carbs={item.carbs_g} protein={item.protein_g} fat={item.fat_g} />

        <div className="mt-4">
          <MacroRow
            testId="nutrition-calories"
            label="Calories"
            value={item.calories}
            unit="kcal"
          />
          <MacroRow
            testId="nutrition-carbs"
            label="Carbohydrates"
            value={item.carbs_g}
            swatch="#C19A6B"
          />
          <MacroRow
            testId="nutrition-protein"
            label="Protein"
            value={item.protein_g}
            swatch="#5A7247"
          />
          <MacroRow
            testId="nutrition-fat"
            label="Fat"
            value={item.fat_g}
            swatch="#C05746"
          />
        </div>

        {!hasMacros && (
          <p
            data-testid="nutrition-no-macros"
            className="mt-3 text-xs text-[#5C625E] italic text-center"
          >
            Detailed macros aren't published for this item yet.
            {item.calories > 0 ? ` Approx ${formatKcal(item.calories)}.` : ""}
          </p>
        )}
      </DialogContent>
    </Dialog>
  );
}
